<template>
    <div class="z-index-2 stress-container">
      <div class="p-3 pb-0 row">
        <div class="wd-60 col" style="min-width: 60%;">
            <div class="mb-0 text-sm text-uppercase font-weight-bold period">{{ period }}</div>
            <h5 style="text-align: left;">Stress</h5>
        </div>
        <div class="wd-40 col">
            <div class="row">
                <div id="range" class="col">
                    <div class="label-name">Range</div>
                    <div class="label-value">{{stressData.minStress}} - {{stressData.maxStress}}</div>
                </div>
                <div id="average" class="col">
                    <div class="label-name">Average</div>
                    <div v-if="avgStressDetails">
                        <span class="label-value">{{ stressData.averageStress }}</span>
                        <span class="label-condition"
                         :style="{background:avgStressDetails.color}">{{ avgStressDetails.label }}</span>
                    </div>
                </div>
                <div id="Latest" class="col">
                    <div class="label-name">Latest</div>
                    <div v-if="latestStressDetails">
                        <span class="label-value">{{stressData.latestStress}}</span>
                        <span class="label-condition"
                          :style="{background:latestStressDetails.color}">{{ latestStressDetails.label }}</span>
                    </div>
                </div>
            </div>
        </div>
      </div>
      <div class="p-3">
        <div>
          <canvas :id="id" class="stress-chart"></canvas>
        </div>
        <div class="status-container d-flex justify-content-center">
            <div class="status-label low">
            <div class="color-box"></div>
            <span>Low</span>
            </div>
            <div class="status-label fair">
            <div class="color-box"></div>
            <span>Fair</span>
            </div>
            <div class="status-label moderate">
            <div class="color-box"></div>
            <span>Moderate</span>
            </div>
            <div class="status-label high">
            <div class="color-box"></div>
            <span>High</span>
            </div>
        </div>
      </div>
    </div>
</template>
  
<script>
  import Chart from "chart.js/auto";
  import annotationPlugin from "chartjs-plugin-annotation";
  import styles from "./UserStressChart.vue?vue&type=style&index=0&lang=scss&module=1";
  import DateUtils from "@/helpers/dateUtils";
  
  Chart.register(annotationPlugin);
  
  export default {
    name: "UserStressChart",
    props: {
      id: {
        type: String,
        default: "usr-stress-chart",
      },
      title: {
        type: String,
        default: "",
      },
      height: {
        type: String,
        default: "16rem",
      },
      stressData: {
        type: Object,
        minStress: Number,
        maxStress: Number,
        latestStress: Number,
        averageStress: Number,
        labels: Array,
        scores: Array,
      },
      selectedDate:{
        default: 'Today'
      }
    },
    data() {
      return {
        avgStressDetails: null,
        latestStressDetails: null,
        period: '',
      };
    },
    watch: {
      stressData() {
        this.addChart();
      },
      selectedDate() {
        this.period = DateUtils.formatPeriod(this.selectedDate);
      }
    },
    mounted() {
      Chart.defaults.borderColor = styles.darkGray;
      this.avgStressDetails = this.getStressDetailsFromScore(this.stressData.averageStress);
      this.latestStressDetails = this.getStressDetailsFromScore(this.stressData.latestStress);
      this.period = DateUtils.formatPeriod(this.selectedDate);
      this.addChart();
    },
    methods: {
      getStressDetailsFromScore(stressScore){
        let stressDetails = null;
        if(stressScore < 30) {
          stressDetails = { label: 'LOW', color: '#7b7878'};
        } else if(stressScore >=30 && stressScore <= 60) {
          stressDetails = { label: 'FAIR', color: '#359685'};
        } else if(stressScore >=60 && stressScore <= 80) {
          stressDetails = { label: 'MODERATE', color: '#f2b252'};
        } else {
          stressDetails = { label: 'HIGH', color: '#f73939'};
        }

        return stressDetails;
      },
      addChart() {
        const ctx = document.getElementById(this.id).getContext("2d");
        let gradientStroke = ctx.createLinearGradient(0, 300, 0, 50);
        
        let rangeArray = [];
        if(this.stressData.minStress >= 0 && this.stressData.minStress < 30) {
          rangeArray.push('#A55EEB');
        }

        if(this.stressData.minStress >= 30 || this.stressData.maxStress < 60) {
          rangeArray.push('#2ACFCF');
        }

        if(this.stressData.minStress >= 60 && this.stressData.maxStress < 80) {
          rangeArray.push('#FFB84E');
        }

        if(this.stressData.maxStress >= 80) {
          rangeArray.push('#ff5a5a');
        }

        if(rangeArray.length == 1) {
          gradientStroke.addColorStop(0, rangeArray[0]);
        } else {
          for(let i = 0; i < rangeArray.length; i++) {
            gradientStroke.addColorStop(1/(rangeArray.length-i), rangeArray[i]);
          }
        }
        let chartStatus = Chart.getChart(this.id);
        if (chartStatus != undefined) {
            chartStatus.destroy();
        }
  
        new Chart(ctx, {
          data: {
           labels: this.stressData.labels, 
           datasets: [
              {
                type: "line",
                label: "Stress Level",
                tension: 0.4,
                borderColor: gradientStroke,
                pointBorderColor: gradientStroke,
                pointBackgroundColor: gradientStroke,
                pointHoverBackgroundColor: gradientStroke,
                pointHoverBorderColor: gradientStroke,
                pointBorderWidth: 0,
                pointHoverRadius: 10,
                pointHoverBorderWidth: 1,
                pointRadius: 0,
                fill: false,
                borderWidth: 1,
                backgroundColor: gradientStroke,
                data: this.stressData.scores,
                order: 1,
              },
            ],
          },
          options: {   
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false
                }
            },
            scales: {
                y: {
                    ticks: {
                        fontColor: "rgba(0,0,0,0.5)",
                        fontStyle: "bold",
                        beginAtZero: true,
                        min: 0,
                        max: 100,
                        stepSize: 20,
                    },
                    gridLines: {
                        drawTicks: false,
                        display: false
                    }
                },
                x: {
                    gridLines: {
                        zeroLineColor: "transparent"
                    },
                    ticks: {
                        padding: 20,
                        fontColor: "rgba(0,0,0,0.5)",
                        fontStyle: "bold"
                    }
                }
            }
         }
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  :export {
    darkRed: $g-red_900;
    red: $g-red_500;
    gray: $color-muted-text;
    darkGray: $color-gridlines;
    colorCalories: $g-red_100;
    colorStrain: $color-strain;
    colorAxes: $color-chart-axes-text;
    colorLimitline: $color-chart-limitline;
  }
  .stress-container {
    background-color: rgb(40, 51, 53);
    border-radius: 0.5rem;
    padding-bottom: 0.5rem;
  }
  .stress-chart {
    background-color: rgb(40, 51, 53);
    height: 16rem;
    width: 100%;
   }
  .period {
    font-size: 0.8rem;
    text-align: left;
    color: #359685;
   }
   .title {
     font-size: 1.2rem;
     font-weight: 100;
     text-align: left;
   }
   .label-name {
     color: rgb(111, 123, 120);
     font-size: 0.8rem;
   }
   .label-value {
    font-size: 1rem;
    font-weight: 100;
    padding-right: 0.2rem;
   }
   .label-condition {
    padding-left: 0.3rem;
    padding-right: 0.3rem;
    // color: #7fd5db;
    background-color: #2B5659;
    font-size: 0.7rem;
    font-weight: bold;
    border-radius: 0.2rem;
   }
   .status-container {
      display: flex;
      gap: 1.5rem; 
      padding-top: 0.5rem;
   }
   .status-label {
     display: flex;
     align-items: center;
     gap: 5px;
   }
   .status-label span {
     font-size: 0.8rem;
   }
   .color-box {
     width: 0.9rem;
     height: 0.9rem;
     border-radius: 0.2rem;
   }
   .low .color-box {
      background-color: #7b7878;
   }
   .fair .color-box {
      background-color: #2ACFCF;
   }

   .moderate .color-box {
      background-color: #FFB84E;
   }

   .high .color-box {
     background-color: #FF5A5A;
   }
</style>
  